import { pipe } from 'fp-ts/function';
import { createElement, useEffect } from 'react';
import { connect } from 'react-redux';
import { initialize } from 'redux-form';
import { Company, Customer, FinancialDocumentPdfTemplateType } from '@mews-commander/data-access';
import { fetchSendEmailDialogData } from '@mews-commander/legacy-entities/src/actions/companies';
import { getCompany } from '@mews-commander/legacy-entities/src/ducks/companies';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { getCustomerOrCompany } from '@mews-commander/legacy-entities/src/selectors/accounts';
import { isAccountIdCompany } from '@mews-commander/legacy-entities/src/selectors/bills';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { ApplicationState, CommanderThunkAction } from '@mews-commander/types';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import {
    useGetAccountIsCompany,
    useGetCompanyAccount,
    useGetCustomerOrCompanyAccount,
} from '../../../../../modules/accounts';
import { openDialog } from '../../../../../modules/dialog/reducer';
import { WithOptionalFirstUsedTemplateType } from '../RepeatedPrintDialogForm';
import { FORM_NAME } from '../SendInvoiceToEmailDialog/constants';
import { REMINDER_DIALOG_NAME } from '../SendReminderToEmailDialog/constants';
import { SendFinancialDocumentToEmailAction } from './types';

interface DispatchProps {
    openEmailDialog: () => void;
    fetchEmailDialogData: () => void;
}

interface StateProps {
    defaultLanguage: string;
}

interface OwnProps {
    action: SendFinancialDocumentToEmailAction;
    showPrintReasonDialog: boolean;
    setShowPrintReasonDialog: (show: boolean) => void;
    component: React.ElementType;
    close: () => void;
}

type SendFinancialDocumentToEmailActionProps = DispatchProps & StateProps & OwnProps;

const SendToEmailAction = ({
    action,
    component,
    openEmailDialog,
    fetchEmailDialogData,
}: SendFinancialDocumentToEmailActionProps) => {
    useEffect(() => {
        fetchEmailDialogData();
    }, [fetchEmailDialogData]);

    return createElement(component, {
        title: action.text,
        icon: action.icon,
        onClick: openEmailDialog,
    });
};

export default pipe(
    SendToEmailAction,
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        (state) => ({
            defaultLanguage: getCurrentEnterpriseDefaultLanguageCode(state),
        }),
        (dispatch, { action, showPrintReasonDialog, setShowPrintReasonDialog }) => ({
            openEmailDialog: () => {
                const {
                    requirePrintReasonOnReprint,
                    isReprint,
                    financialDocumentId,
                    documentType,
                    ownerId,
                    companyDetailsId,
                    requirePrintReasonOnTemplateChange,
                    firstUsedTemplateType,
                    allowedTemplateTypes,
                } = action;

                dispatch(
                    // @ts-expect-error $TSFixMeError: Argument of type 'ThunkAction<Action<any>, void, ApplicationState>' is not assignable to parameter of type 'Action<any>'...
                    openSendEmailDialog({
                        financialDocumentId,
                        documentType,
                        ownerId,
                        companyId: companyDetailsId,
                        showPrintReason: isReprint || showPrintReasonDialog,
                        setShowPrintReason: requirePrintReasonOnReprint ? setShowPrintReasonDialog : undefined,
                        firstUsedTemplateType,
                        requirePrintReasonOnTemplateChange,
                        allowedTemplateTypes,
                        type: 'SendBill',
                    })
                );
            },
            fetchEmailDialogData: () =>
                // @ts-expect-error $TSFixMeError: Argument of type 'ThunkAction<Action<any>, void, ApplicationState>' is not assignable to parameter of type 'Action<any>'...
                dispatch(fetchSendEmailDialogData({ ownerId: action.ownerId, companyId: action.companyDetailsId })),
        })
    )
);

interface OpenSendEmailDialogProps extends WithOptionalFirstUsedTemplateType {
    financialDocumentId: string;
    documentType: SendFinancialDocumentToEmailAction['documentType'];
    ownerId: string;
    companyId: string;
    showPrintReason: boolean;
    setShowPrintReason?: (show: boolean) => void;
    requirePrintReasonOnTemplateChange?: boolean;
    allowedTemplateTypes?: FinancialDocumentPdfTemplateType[];
    lastUsedTemplateType?: FinancialDocumentPdfTemplateType;
    type: SendEmailType;
}

export type SendEmailType = 'SendReminder' | 'SendBill';

export const openSendEmailDialog =
    ({
        financialDocumentId,
        documentType,
        ownerId,
        companyId,
        showPrintReason,
        setShowPrintReason,
        requirePrintReasonOnTemplateChange,
        firstUsedTemplateType,
        allowedTemplateTypes,
        type,
        lastUsedTemplateType,
    }: OpenSendEmailDialogProps): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const defaultLanguage = getCurrentEnterpriseDefaultLanguageCode(state);
        const owner = getCustomerOrCompany(state, { id: ownerId });
        const isOwnerCompany = isAccountIdCompany(state, { accountId: ownerId });
        const hasPreferredLanguage = owner && 'preferredLanguageCode' in owner && owner?.preferredLanguageCode;
        const ownerLanguage = hasPreferredLanguage ? owner?.preferredLanguageCode : defaultLanguage;
        const ownerEmail = owner?.email;
        const companyEmail = getCompany(state, { id: companyId })?.email;
        const noPredefinedEmail = !ownerEmail && !companyEmail;

        dispatch(
            initialize(FORM_NAME, {
                financialDocumentId,
                documentType,
                language: ownerLanguage,
                defaultEmails: [],
                isOwnerCompany,
                emails: noPredefinedEmail ? [{ value: '' }] : [],
                ownerEmail,
                companyEmail,
                showPrintReason,
                setShowPrintReason,
                template:
                    type === 'SendReminder' && lastUsedTemplateType
                        ? lastUsedTemplateType
                        : FinancialDocumentPdfTemplateType.Detailed,
                requirePrintReasonOnTemplateChange,
                firstUsedTemplateType,
                allowedTemplateTypes,
            })
        );
        switch (type) {
            case 'SendBill':
                dispatch(openDialog(FORM_NAME));
                break;
            case 'SendReminder':
                dispatch(openDialog(REMINDER_DIALOG_NAME));
                break;
        }
    };

export const useOpenSendEmailDialog = ({
    ownerId,
    companyId,
    financialDocumentId,
    documentType,
    type,
    showPrintReason,
    setShowPrintReason,
    lastUsedTemplateType,
    requirePrintReasonOnTemplateChange,
    firstUsedTemplateType,
    allowedTemplateTypes,
}: OpenSendEmailDialogProps) => {
    const dispatch = useCommanderThunkDispatch();
    const { refetch: refetchOwner } = useGetCustomerOrCompanyAccount({
        accountId: ownerId,
        enabled: false,
    });
    const { defaultLanguageCode } = useCurrentEnterprise();
    const { refetch: refetchIsOwnerCompany } = useGetAccountIsCompany({
        accountId: ownerId,
        enabled: false,
    });
    const { refetch: refetchCompany } = useGetCompanyAccount({ accountId: companyId, enabled: false });

    return async () => {
        const { data: owner } = await refetchOwner();
        const { data: isOwnerCompany } = await refetchIsOwnerCompany();
        const companyQuery = companyId ? await refetchCompany() : null;
        const { data: company } = companyQuery || {};

        const ownerPreferredLanguageCode = owner && hasPreferredLanguageCode(owner) && owner?.preferredLanguageCode;
        const language = ownerPreferredLanguageCode || defaultLanguageCode;
        const ownerEmail = owner?.email;
        const companyEmail = company?.email;

        const noPredefinedEmail = !ownerEmail && !companyEmail;

        dispatch(
            initialize(FORM_NAME, {
                financialDocumentId,
                documentType,
                language,
                defaultEmails: [],
                isOwnerCompany,
                emails: noPredefinedEmail ? [{ value: '' }] : [],
                ownerEmail,
                companyEmail,
                showPrintReason,
                setShowPrintReason,
                template:
                    type === 'SendReminder' && lastUsedTemplateType
                        ? lastUsedTemplateType
                        : FinancialDocumentPdfTemplateType.Detailed,
                requirePrintReasonOnTemplateChange,
                firstUsedTemplateType,
                allowedTemplateTypes,
            })
        );

        const dialogName = type === 'SendBill' ? FORM_NAME : REMINDER_DIALOG_NAME;
        dispatch(openDialog(dialogName));
    };
};

const hasPreferredLanguageCode = (owner: Customer | Company): owner is Customer => 'preferredLanguageCode' in owner;
